import { useState } from "react";

import { Button } from "../../components/Button";
import { Dropzone } from "../../components/Dropzone";
import type { ImportKind } from "../../types/api";

interface UploadStepProps {
  shipmentFile: File | null;
  invoiceFile: File | null;
  shipmentError: string | null;
  invoiceError: string | null;
  busy: boolean;
  onFile: (kind: ImportKind, file: File | null) => void;
  onContinue: () => void;
}

async function fetchSample(name: string): Promise<File> {
  const response = await fetch(`${import.meta.env.BASE_URL}samples/${name}`);
  if (!response.ok) throw new Error(`Sample ${name} is unavailable`);
  return new File([await response.blob()], name, {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

export function UploadStep(props: UploadStepProps) {
  const { shipmentFile, invoiceFile, shipmentError, invoiceError, busy, onFile, onContinue } = props;
  const [sampleError, setSampleError] = useState<string | null>(null);
  const [loadingSamples, setLoadingSamples] = useState(false);
  const ready = Boolean(shipmentFile && invoiceFile && !shipmentError && !invoiceError);

  async function useSamples() {
    setLoadingSamples(true);
    setSampleError(null);
    try {
      const [shipments, invoices] = await Promise.all([fetchSample("shipments.xlsx"), fetchSample("invoices.xlsx")]);
      onFile("shipment", shipments);
      onFile("invoice", invoices);
    } catch {
      setSampleError("The sample files couldn't be loaded. Use your own files instead.");
    } finally {
      setLoadingSamples(false);
    }
  }

  return (
    <section aria-labelledby="upload-heading">
      <div className="hero">
        <p className="eyebrow">Shipment-to-invoice control</p>
        <h1 id="upload-heading">Find freight invoice exceptions before they hit AP.</h1>
        <p className="hero__lede">
          Drop your shipment or quote ledger and the carrier&apos;s invoice export. FreightAudit matches every PRO,
          checks totals, weights, duplicates and accessorials, and hands you only the rows that need a human.
        </p>
      </div>

      <div className="panel upload-panel">
        <div className="upload-grid">
          <Dropzone
            label="Shipment / quote file"
            description="Needs a PRO number, quoted total and quoted weight for each shipment."
            file={shipmentFile}
            error={shipmentError}
            disabled={busy}
            onFile={(file) => onFile("shipment", file)}
          />
          <Dropzone
            label="Carrier invoice file"
            description="Needs PRO number, invoice number, billed total and billed weight."
            file={invoiceFile}
            error={invoiceError}
            disabled={busy}
            onFile={(file) => onFile("invoice", file)}
          />
        </div>
        <div className="panel__footer">
          <Button variant="ghost" size="sm" onClick={useSamples} busy={loadingSamples} disabled={busy}>
            Try it with sample files
          </Button>
          <Button variant="primary" iconAfter="arrow-right" onClick={onContinue} disabled={!ready} busy={busy}>
            {busy ? "Reading files…" : "Continue"}
          </Button>
        </div>
        {sampleError ? (
          <p className="field-error" role="alert">
            {sampleError}
          </p>
        ) : null}
      </div>

      <ul className="assurances" aria-label="How your files are handled">
        <li>Column names don&apos;t need to match: you confirm the mapping next.</li>
        <li>Uploaded spreadsheets are deleted as soon as the audit is built.</li>
        <li>Nothing is paid, disputed or emailed for you. You decide every exception.</li>
      </ul>
    </section>
  );
}
